import type { operations } from "@/api/gerado/admin";
import type { Severidade } from "@/componentes/Confirmacao";

/*
 * Os escopos que a tela oferece (design D4).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A TELA É MAIS ESTREITA QUE O CATÁLOGO, E `admin:*` NÃO ENTRA.            │
 * │                                                                          │
 * │ `admin:*` concede tudo, e hoje só sai por chamada direta à rota. Pôr na  │
 * │ tela seria ampliar com um clique o que o papel concede. O `Exclude`      │
 * │ reprova na compilação quem o acrescentar, e o teste reprova o resto.     │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A lista mora aqui, e não vem de uma rota: o catálogo traria `admin:*`, e a tela teria de
 * filtrar do mesmo jeito.
 */
type EscopoDoCatalogo =
  operations["emitirChave"]["requestBody"]["content"]["application/json"]["escopos"][number];

export type EscopoDaTela = Exclude<EscopoDoCatalogo, "admin:*">;
export type AmbienteDaChave = "test" | "live";

export const ESCOPOS_DA_TELA = [
  { escopo: "emissao:write", dica: "Emite e cancela boleto." },
  { escopo: "boleto:read", dica: "Consulta boleto e baixa documento." },
  {
    escopo: "cadastro:write",
    dica: "Cria cedente, conta e convênio pela API, e a conta é destino de dinheiro. Para ler o cadastro, a chave precisa também de boleto:read.",
  },
] as const satisfies readonly { escopo: EscopoDaTela; dica: string }[];

/** `cadastro:write` fica de fora: nunca é concedido por omissão. */
export const ESCOPOS_INICIAIS: readonly EscopoDaTela[] = ["emissao:write", "boleto:read"];

const ESCOPO_DE_CADASTRO: EscopoDaTela = "cadastro:write";

/** Sempre na ordem do catálogo, para a confirmação e o corpo enviado não dependerem do clique. */
export function naOrdemDoCatalogo(escopos: readonly EscopoDaTela[]): EscopoDaTela[] {
  return ESCOPOS_DA_TELA.map(({ escopo }) => escopo).filter((escopo) => escopos.includes(escopo));
}

/*
 * A confirmação acompanha o poder da chave: `live` emite de verdade, e `cadastro:write` cria
 * conta de destino de dinheiro. Uma chave `test` com esse escopo continua sendo decisão.
 */
export function severidadeDaChave(
  ambiente: AmbienteDaChave,
  escopos: readonly EscopoDaTela[],
): Extract<Severidade, "destrutiva" | "critica"> {
  return ambiente === "live" || escopos.includes(ESCOPO_DE_CADASTRO) ? "critica" : "destrutiva";
}

const UMA_VEZ = "A chave aparece uma única vez.";

/** O efeito, em até duas frases. Chave `live` só de leitura não emite, e não pode dizer que emite. */
export function efeitoDaChave(
  ambiente: AmbienteDaChave,
  escopos: readonly EscopoDaTela[],
): string {
  const emite = ambiente === "live" && escopos.includes("emissao:write");
  const cadastra = escopos.includes(ESCOPO_DE_CADASTRO);

  if (emite && cadastra) {
    return `Emite boleto real e cria cedente e conta de destino do dinheiro. ${UMA_VEZ}`;
  }
  if (emite) return `Emite boleto real. ${UMA_VEZ}`;
  if (cadastra) return `Cria cedente e conta de destino do dinheiro. ${UMA_VEZ}`;
  return UMA_VEZ;
}
